import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/base/Button';
import type { NavigatorBannerContent } from '@/types/service-directory';

/**
 * Props interface for NavigatorBanner component
 */
export interface NavigatorBannerProps {
  /** Banner content */
  content?: NavigatorBannerContent;
  /** Whether the viewport is mobile (< 800px) */
  isMobile?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * NavigatorBanner - CTA banner for Quality Care Navigator
 *
 * Displays a promotional banner with screenshot, title, subtitle,
 * and a CTA button linking to the Quality Care Navigator.
 *
 * @example
 * ```tsx
 * import { NavigatorBanner } from '@/components';
 *
 * <NavigatorBanner content={navigatorBannerContent} />
 * ```
 */
export const NavigatorBanner = forwardRef<HTMLDivElement, NavigatorBannerProps>(
  ({ content, isMobile, className }, ref) => {
    if (!content) return null;

    return (
      <div ref={ref} className={cn('w-full', isMobile ? 'px-4' : 'px-10', className)}>
        <div
          className={cn(
            'w-full max-w-[1360px] mx-auto',
            'bg-[var(--color-bg-gray)]',
            'rounded-[4px] p-6',
            'border border-[var(--color-border-default)]',
            'shadow-[var(--shadow-card)]',
            isMobile
              ? 'flex flex-col gap-4'
              : 'flex items-center gap-8'
          )}
          role="region"
          aria-label="Quality Care Navigator promotion"
        >
          {/* Screenshot image */}
          <div className={cn(isMobile ? 'w-full flex justify-center' : 'shrink-0')}>
            <img
              src={content.screenshot.src}
              alt=""
              className={cn(
                'h-auto rounded-[4px] shadow-sm',
                isMobile ? 'w-full max-w-[400px]' : 'w-[280px]'
              )}
            />
          </div>

          {/* Text content */}
          <div className="flex-1 flex flex-col gap-2">
            <h2
              className={cn(
                'text-2xl leading-8 font-semibold',
                "font-['Source_Sans_Pro']",
                'text-foreground'
              )}
            >
              {content.title}
            </h2>
            <p
              className={cn(
                'text-base leading-6',
                "font-['Source_Sans_Pro']",
                'text-[var(--color-text-secondary)]'
              )}
            >
              {content.subtitle}
            </p>
            {/* CTA button */}
            <Button
              variant="secondary"
              size="default"
              content={{
                text: content.button.text,
                icon: 'ArrowRight',
              }}
              onClick={() => window.open(content.button.href, '_blank', 'noopener,noreferrer')}
              aria-label={`${content.button.text} - opens in new tab`}
              className={cn('mt-2', isMobile ? 'w-full' : 'self-start')}
            />
          </div>
        </div>
      </div>
    );
  }
);

NavigatorBanner.displayName = 'NavigatorBanner';
